import { useCallback, useEffect, useState } from "react"
import { coreStore } from "@forcepoint/platform-coreui"
import { CaretIcon } from "./CaretIcon"
import { subMenuItemClick } from "./Sidebar.utils"
import { SubMenuProps } from "./Sidebar.types"
import { toIdToken, toMenuToken } from "./menu-id"

const SubMenu = ({ subMenus, currentApplication, idPrefix }: SubMenuProps) => {
  /**
   * Active menu
   */
  const [activeMenu, setActiveMenu] = useState<any>(
    coreStore.getState()?.activeMenu
  )
  const activeApp = coreStore.getState()?.activeApp

  /**
   * On Init use effect
   */
  useEffect(() => {
    const navigationSubscription = coreStore.subscribe(() => {
      const state = coreStore.getState()
      setActiveMenu(state.activeMenu)
    })
    return () => {
      navigationSubscription()
    }
  }, [])

  // Root of the id chain for this app's menu tree. Nested levels append
  // their own token to the parent's id (passed down as idPrefix) so that a
  // name repeated at different points in one app's tree — the contract
  // allows it; nothing enforces unique name/localizationKey — still yields
  // a unique, hierarchical id.
  const baseId =
    idPrefix || `sub-menu-${toIdToken(currentApplication?.applicationCode)}`

  /**
   * UI Template
   */
  return (
    <div className="sub-menu-list-container">
      {subMenus.map((subMenuItem) => (
        <SubMenuItem
          key={subMenuItem?.name}
          subMenuItem={subMenuItem}
          activeMenu={activeMenu}
          currentApplication={currentApplication}
          activeApp={activeApp}
          idPrefix={baseId}
        />
      ))}
    </div>
  )
}

export default SubMenu

const SubMenuItem = ({
  subMenuItem,
  activeMenu,
  currentApplication,
  activeApp,
  idPrefix
}) => {
  const [nestedMenuOpen, setNestedMenuOpen] = useState(false)
  const [currentPath, setCurrentPath] = useState(window.location.pathname)
  const activeAppCode = activeApp?.applicationCode || ""
  const activeMenuName = activeAppCode + activeMenu?.name

  useEffect(() => {
    const onRouteChange = () => setCurrentPath(window.location.pathname)
    window.addEventListener("single-spa:routing-event", onRouteChange)
    return () => window.removeEventListener("single-spa:routing-event", onRouteChange)
  }, [])
  useEffect(() => {
    const checkIfMenuShouldBeOpen = (menu, activeMenuName) => {
      const openedMenuName = currentApplication.applicationCode + menu.name
      if (openedMenuName === activeMenuName) {
        return true
      }
      if (menu.menus) {
        return menu.menus.some((subMenu) =>
          checkIfMenuShouldBeOpen(subMenu, activeMenuName)
        )
      }
      return false
    }
    if (
      activeMenuName &&
      checkIfMenuShouldBeOpen(subMenuItem, activeMenuName)
    ) {
      setNestedMenuOpen(true)
    }
  }, [])

  const menuId = `${idPrefix}-${toMenuToken(subMenuItem)}`

  const getActiveMenuClassName = useCallback(
    (menu) => {
      // Early return if no menu
      if (!menu) return null

      let openedMenuName: string

      // Determine the correct menu name based on application context
      if (currentApplication?.applicationCode === "settings") {
        // For settings menus, check if this menu exists in active app's settings
        const settingsMenu = activeApp?.settings?.find(
          (setting) => setting.name === menu.name
        )

        if (!settingsMenu) return null

        openedMenuName = activeAppCode + menu.name
      } else {
        // For non-settings menus, use current application code
        openedMenuName = currentApplication?.applicationCode + menu.name
      }

      // Check if this menu is the active one
      if (openedMenuName === activeMenuName) {
        return "active-menu-item"
      }

      // Fallback: URL-based check for SPA apps whose menus are not in state.applications (e.g. Home)
      if (!currentApplication?.openInIframe && currentPath === menu.url) {
        return "active-menu-item"
      }

      // Check nested menus recursively
      if (menu.menus?.length > 0) {
        for (const subMenu of menu.menus) {
          const result = getActiveMenuClassName(subMenu)
          if (result) {
            return "active-child-menu-item"
          }
        }
      }

      return null
    },
    [activeMenuName, currentPath]
  )

  return (
    <div
      key={subMenuItem?.name}
      className={`sub-menu ${getActiveMenuClassName(subMenuItem)}`}
      data-testid={`sub-menu-${subMenuItem?.localizationKey || subMenuItem?.name}`}
      // Unique DOM selector for Gainsight PX — a native `id` (unlike a
      // made-up data-* attribute) is what PX's own auto-track/visual
      // tagging actually keys off when generating its selector for an
      // element, so it needs to be app-scoped to disambiguate identically
      // named menus across apps (e.g. "Home" in RBI vs Insights renders
      // the exact same DOM structure/classes otherwise) — the ambiguity
      // flagged in FPN-6545. Left data-testid untouched: QA automation
      // (platformAutomation/idmAutomation Cypress locators) depends on
      // its existing, app-unscoped value.
      id={menuId}
      onClick={(ev) => {
        subMenuItemClick(subMenuItem, currentApplication, setNestedMenuOpen)
        ev.stopPropagation()
      }}
      onKeyDown={() => {}}
    >
      <div
        className={`sub-menu-item ${getActiveMenuClassName(subMenuItem)}`}
        onKeyDown={() => {}}
      >
        <span className="sub-menu-item-name">{subMenuItem?.localizationKey || subMenuItem?.name}</span>
        {subMenuItem?.menus && subMenuItem.menus.length > 0 && (
          <CaretIcon isOpen={nestedMenuOpen}></CaretIcon>
        )}
      </div>
      {subMenuItem?.menus && subMenuItem.menus.length > 0 && nestedMenuOpen && (
        <div className="nested-sub-menu">
          <SubMenu
            subMenus={subMenuItem?.menus}
            currentApplication={currentApplication}
            idPrefix={menuId}
          />
        </div>
      )}
    </div>
  )
}
